import React, { useState, useEffect } from 'react';
import syncEngine from '../services/syncEngine';
import './OfflineIndicator.css';

const OfflineIndicator = () => {
  const [syncStatus, setSyncStatus] = useState({
    isOnline: navigator.onLine,
    isSyncing: false,
    cloudSyncEnabled: false,
    lastSyncAt: null,
    pendingCount: 0,
  });

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    loadSyncStatus();

    // Listen for sync events
    const handleSyncEvent = (event) => {
      switch (event.type) {
        case 'online':
          showNotification('Back online - Syncing...');
          break;
        case 'offline':
          showNotification('You are offline');
          break;
        case 'sync_started':
          showNotification('Syncing...');
          break;
        case 'sync_completed':
          showNotification('Sync completed');
          break;
        case 'sync_failed':
          showNotification('Sync failed');
          break;
        default:
          break;
      }

      loadSyncStatus();
    };

    syncEngine.addListener(handleSyncEvent);

    // Poll sync status every 30 seconds
    const interval = setInterval(loadSyncStatus, 30000);

    return () => {
      syncEngine.removeListener(handleSyncEvent);
      clearInterval(interval);
    };
  }, []);

  const loadSyncStatus = async () => {
    const status = await syncEngine.getSyncStatus();
    if (status) {
      setSyncStatus(status);
    }
  };

  const showNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleSyncNow = async () => {
    try {
      await syncEngine.syncNow();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  };

  const formatLastSync = (lastSyncAt) => {
    if (!lastSyncAt) return 'Never';

    const now = new Date();
    const syncDate = new Date(lastSyncAt);
    const diffMs = now - syncDate;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <>
      {/* Offline/Online Indicator */}
      <div className={`offline-indicator ${syncStatus.isOnline ? 'online' : 'offline'}`}>
        <div className="indicator-icon">
          {syncStatus.isSyncing ? (
            <span className="sync-spinner">⟳</span>
          ) : syncStatus.isOnline ? (
            <span>✓</span>
          ) : (
            <span>⚠</span>
          )}
        </div>

        <div className="indicator-content">
          <span className="indicator-status">
            {syncStatus.isSyncing
              ? 'Syncing...'
              : syncStatus.isOnline
              ? 'Online'
              : 'Offline'}
          </span>

          {syncStatus.cloudSyncEnabled && syncStatus.isOnline && !syncStatus.isSyncing && (
            <span className="indicator-detail">
              {syncStatus.pendingCount > 0 && (
                <span className="pending-count">{syncStatus.pendingCount} pending</span>
              )}
              {syncStatus.lastSyncAt && (
                <span className="last-sync">
                  Synced {formatLastSync(syncStatus.lastSyncAt)}
                </span>
              )}
            </span>
          )}

          {syncStatus.cloudSyncEnabled && syncStatus.isOnline && !syncStatus.isSyncing && (
            <button className="sync-now-btn" onClick={handleSyncNow} title="Sync now">
              ⟳
            </button>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="sync-toast">
          <span>{toastMessage}</span>
        </div>
      )}
    </>
  );
};

export default OfflineIndicator;
